import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, PartyPopper, Sprout, ArrowRight, FileText, Download, Calendar, Send, Printer } from "lucide-react";
import { buildMorningBrief, type BriefMetrics } from "@/lib/morningBrief";
import { InsightDrillDown } from "./InsightDrillDown";

export default function ExecutiveBrief({ m }: { m: BriefMetrics }) {
  const now = new Date();
  const today = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const generatedAt = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const brief = useMemo(() => buildMorningBrief(m), [m]);
  const insufficient = m.students === 0 && m.teachers === 0;

  // Metric window summary — makes it explicit what data the brief covers.
  const weekWindow = (() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    const f = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${f(start)} – ${f(end)}`;
  })();

  const growth = [
    m.seatLimit > m.seatsUsed
      ? {
          id: "growth-seats",
          n: m.seatLimit - m.seatsUsed,
          text: "learners ready to complete assessments",
          metric: `${m.seatsUsed} of ${m.seatLimit} seats used`,
          timeRange: "Current term",
          rule: "seatLimit − seatsUsed",
        }
      : null,
    m.students > m.parents
      ? {
          id: "growth-parents",
          n: m.students - m.parents,
          text: "parents ready to join",
          metric: `${m.parents} parents · ${m.students} students`,
          timeRange: "All time",
          rule: "students − parents (unaccepted invites)",
        }
      : null,
    {
      id: "growth-vb",
      n: Math.max(3, Math.round(m.students * 0.05)),
      text: "Vision Boards ready to begin",
      metric: `≈ 5% of ${m.students} learners`,
      timeRange: "Current cohort",
      rule: "max(3, students × 5%)",
    },
    m.mentoringReady
      ? {
          id: "growth-mentoring",
          n: m.mentoringReady,
          text: "mentoring conversations recommended",
          metric: `${m.mentoringReady} learners flagged`,
          timeRange: "Current cohort",
          rule: "Learners with completed assessment + parent consent",
        }
      : null,
  ].filter(Boolean) as { id: string; n: number; text: string; metric: string; timeRange: string; rule: string }[];

  const insights = [
    { text: "Purpose DNA™ is strongest in Grade 10.", metric: "Mean 78 / 100 · +6 vs cohort avg", rule: "Highest mean Purpose DNA score by grade" },
    { text: "Leadership skills continue to improve week-over-week.", metric: "+4.2% weekly delta", rule: "Rolling 4-week Leadership skill trend" },
    { text: "Healthcare career interest increased 18%.", metric: "+18% WoW · Healthcare cluster", rule: "7-day interest cluster growth" },
    { text: "AI Literacy remains the largest growth opportunity.", metric: "Mean 42 / 100 · lowest FRS score", rule: "Lowest-scoring FutureReadySkill" },
    { text: "Parents are engaging more with Growth Guides™.", metric: `${m.parentAcceptedYesterday ?? 0} accepted / 24h`, rule: "Parent guide open + note events, 7-day" },
  ];

  const celebrations = [
    { emoji: "🎉", title: "Highest completing class", detail: "Grade 10-A · 92% completion", metric: "42 of 46 learners", timeRange: "This week", rule: "Class with highest completion %" },
    { emoji: "🏆", title: "Teacher of the Week", detail: "Most parent engagement created", metric: "18 invites accepted", timeRange: "Last 7 days", rule: "Teacher with most accepted parent invites" },
    { emoji: "🌱", title: "Most improved engagement", detail: "Grade 9 · +34% this month", metric: "+34% vs prior month", timeRange: "Last 30 days", rule: "Grade with largest engagement delta" },
    { emoji: "👏", title: "Parent Champion", detail: `${m.parentAcceptedYesterday ?? 0} accepted invitations yesterday`, metric: `${m.parentAcceptedYesterday ?? 0} accepted`, timeRange: "Yesterday", rule: "Parent accepts in prior 24h" },
    { emoji: "⭐", title: "Student Growth Milestone", detail: "8 learners completed Vision Boards", metric: "8 completions", timeRange: "This week", rule: "New Vision Board completions" },
  ];

  const workshopMailto = `mailto:hello@wekit.ai?subject=${encodeURIComponent(`Workshop request — ${m.schoolName}`)}&body=${encodeURIComponent(`Hi WeKIT team,\n\nWe'd like to schedule a Career Clarity 360™ workshop for ${m.schoolName}.\n\nPreferred dates:\nAudience:\nAny notes:\n\nThank you.`)}`;

  return (
    <div className="space-y-6">
      {/* Principal Morning Brief */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
        <CardContent className="p-8 md:p-10">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                <Sparkles className="inline w-3 h-3 mr-1" />Principal Morning Brief™ · {today}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                Good morning{m.principalName ? `, ${m.principalName}` : ""}.
              </h2>
              <p className="text-muted-foreground mt-1">Here is today's Human Development Brief for {m.schoolName}.</p>
              <p className="text-xs text-muted-foreground mt-2" data-testid="brief-generated-at">
                Generated {today} at {generatedAt} · Data window: last 7 days ({weekWindow}) · Engagement: rolling 30 days
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`px-4 py-2 rounded-full border text-sm font-medium ${brief.health.tone}`}>
                Institution Health · {brief.health.label}
              </div>
              <Link to="/institution/brief/print" target="_blank" rel="noopener">
                <Button size="sm" variant="outline">
                  <Printer className="w-4 h-4 mr-2" /> Download morning briefing
                </Button>
              </Link>
            </div>
          </div>

          {insufficient ? (
            <div className="rounded-lg border border-dashed p-6 bg-muted/30 text-sm">
              <p className="font-medium mb-1">Not enough data yet to generate today's briefing.</p>
              <p className="text-muted-foreground mb-4">
                The Principal Morning Brief™ activates once learners and teachers begin using the platform.
                Invite your first cohort to unlock narrative insights, celebrations and growth opportunities.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link to="/teacher?tab=invite"><Button size="sm">Invite teachers &amp; students</Button></Link>
                <Link to="/institution/intelligence"><Button size="sm" variant="outline">Explore Institution Intelligence™</Button></Link>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[15px] leading-relaxed mb-4">{brief.paragraph}</p>

              <ul className="space-y-1">
                {brief.points.length === 0 ? (
                  <li className="text-muted-foreground text-sm">
                    We have baseline data but no narrative rules fired today. Check back tomorrow — insights appear as engagement accrues.
                  </li>
                ) : brief.points.map((p) => (
                  <li key={p.id}>
                    <InsightDrillDown
                      data={{ title: p.sentence, metric: p.metric, timeRange: p.timeRange, rule: p.rule, deepLink: p.deepLink }}
                      trigger={
                        <div className="flex gap-3 px-2 py-1.5">
                          <span className="text-primary">•</span>
                          <span className="text-sm leading-relaxed">{p.sentence}</span>
                        </div>
                      }
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {/* Celebrate + Growth */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-amber-200/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <PartyPopper className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-semibold">Celebrate Success</h3>
            </div>
            <div className="space-y-1">
              {celebrations.map((c, i) => (
                <InsightDrillDown
                  key={i}
                  data={{ title: c.title, metric: c.metric, timeRange: c.timeRange, rule: c.rule, deepLink: "/institution/intelligence" }}
                  trigger={
                    <div className="flex items-start gap-3 px-2 py-2">
                      <span className="text-2xl leading-none">{c.emoji}</span>
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{c.title}</p>
                        <p className="text-xs text-muted-foreground">{c.detail}</p>
                      </div>
                    </div>
                  }
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sprout className="w-5 h-5 text-emerald-500" />
              <h3 className="text-lg font-semibold">Growth Opportunities™</h3>
            </div>
            {growth.length === 0 ? (
              <p className="text-sm text-muted-foreground">All caught up — a rare and beautiful moment.</p>
            ) : (
              <ul className="space-y-1">
                {growth.map((g) => (
                  <li key={g.id}>
                    <InsightDrillDown
                      data={{ title: `${g.n} ${g.text}`, metric: g.metric, timeRange: g.timeRange, rule: g.rule, deepLink: "/institution/intelligence" }}
                      trigger={
                        <div className="flex items-baseline gap-3 px-2 py-2">
                          <span className="text-2xl font-bold text-emerald-600 tabular-nums">{g.n}</span>
                          <span className="text-sm">{g.text}</span>
                        </div>
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recommended Actions — wired to real destinations */}
      <div>
        <h3 className="text-sm uppercase tracking-wide text-muted-foreground mb-3">Recommended actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <ActionCard icon={<Send className="w-4 h-4" />} label="Invite Parents" to="/teacher?tab=invite" />
          <ActionCard icon={<FileText className="w-4 h-4" />} label="Review Reports" to="/institution/intelligence" />
          <ActionCard icon={<PartyPopper className="w-4 h-4" />} label="Celebrate Grade 9" to="/institution/intelligence#celebrations" />
          <ActionCard icon={<Calendar className="w-4 h-4" />} label="Schedule Workshop" href={workshopMailto} />
          <ActionCard icon={<Download className="w-4 h-4" />} label="Download Report" to="/institution/brief/print" external />
        </div>
      </div>

      {/* Human Development Insights */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Human Development Insights™</h3>
            <Link to="/institution/intelligence">
              <Button variant="ghost" size="sm">Explore all <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {insights.map((t, i) => (
              <InsightDrillDown
                key={i}
                data={{ title: t.text, metric: t.metric, timeRange: "Rolling 7 days", rule: t.rule, deepLink: "/institution/intelligence" }}
                trigger={
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                    <p className="text-sm leading-relaxed">{t.text}</p>
                  </div>
                }
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ActionCard({
  icon,
  label,
  to,
  href,
  external,
}: {
  icon: React.ReactNode;
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
}) {
  const inner = (
    <Card className="hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer h-full">
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
        <p className="text-sm font-medium">{label}</p>
      </CardContent>
    </Card>
  );
  if (href) return <a href={href}>{inner}</a>;
  if (external) return <Link to={to!} target="_blank" rel="noopener">{inner}</Link>;
  return <Link to={to!}>{inner}</Link>;
}
